import {
  computed,
  isRef,
  ref,
  shallowRef,
  toValue,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
  type ShallowRef,
} from 'vue'

import type { ReportColumn } from '../models/column.types'
import type { FilterDropdownables, ReportFilter } from '../models/filter.types'
import type { ReportRow } from '../models/report.types'
import { dropdownablesFromRows } from '../service/dropdownablesFromRows'
import { filterRows } from '../service/filterRows'

type ReportColumnsSource = MaybeRefOrGetter<readonly ReportColumn[] | undefined>

export interface UseReportFiltersOptions {
  initialFilters?: MaybeRefOrGetter<readonly ReportFilter[] | undefined>
  initialQuery?: MaybeRefOrGetter<string | undefined>
  rows?: MaybeRefOrGetter<readonly ReportRow[] | undefined>
  dropdownables?: MaybeRefOrGetter<FilterDropdownables | null | undefined>
  maxDropdownValues?: number
}

export interface UseReportFilters {
  filters: ShallowRef<ReportFilter[]>
  query: Ref<string>
  dropdownables: ComputedRef<FilterDropdownables>
  setFilters: (filters: readonly ReportFilter[]) => void
  setQuery: (query: string) => void
  addFilter: (filter: ReportFilter) => void
  updateFilter: (index: number, patch: Partial<ReportFilter>) => void
  removeFilter: (index: number) => void
  clearFilters: () => void
  resetFilters: () => void
  filtered: (rows?: readonly ReportRow[]) => ReportRow[]
}

function cloneFilter(filter: ReportFilter): ReportFilter {
  return {
    ...filter,
    value: Array.isArray(filter.value) ? [...filter.value] : filter.value,
  }
}

function cloneFilters(filters: readonly ReportFilter[]): ReportFilter[] {
  return filters.map(cloneFilter)
}

function isColumnsSource(
  value: ReportColumnsSource | UseReportFiltersOptions | undefined,
): value is ReportColumnsSource {
  return Array.isArray(value) || typeof value === 'function' || isRef(value)
}

export function useReportFilters(options?: UseReportFiltersOptions): UseReportFilters
export function useReportFilters(
  columns: ReportColumnsSource,
  options?: UseReportFiltersOptions,
): UseReportFilters
/** Local report-filter state shared by ReportFilters consumers without coupling to persistence. */
export function useReportFilters(
  columnsOrOptions?: ReportColumnsSource | UseReportFiltersOptions,
  passedOptions?: UseReportFiltersOptions,
): UseReportFilters {
  const hasColumnArgument = passedOptions !== undefined || isColumnsSource(columnsOrOptions)
  const columns = hasColumnArgument ? (columnsOrOptions as ReportColumnsSource) : undefined
  const options: UseReportFiltersOptions = hasColumnArgument
    ? (passedOptions ?? {})
    : ((columnsOrOptions ?? {}) as UseReportFiltersOptions)
  const initial = (): ReportFilter[] => cloneFilters(toValue(options.initialFilters) ?? [])
  const filters = shallowRef<ReportFilter[]>(initial())
  const query = ref(toValue(options.initialQuery) ?? '')
  const dropdownables = computed<FilterDropdownables>(() => {
    const provided = toValue(options.dropdownables)
    if (provided) return provided

    const maxValues = options.maxDropdownValues
    return dropdownablesFromRows(
      toValue(options.rows) ?? [],
      toValue(columns) ?? [],
      maxValues === undefined ? {} : { maxValues },
    )
  })

  return {
    filters,
    query,
    dropdownables,
    setFilters: (value) => {
      filters.value = cloneFilters(value)
    },
    setQuery: (value) => {
      query.value = value
    },
    addFilter: (filter) => {
      filters.value = [...filters.value, cloneFilter(filter)]
    },
    updateFilter: (index, patch) => {
      const current = filters.value[index]
      if (!current) return
      filters.value = filters.value.map((filter, currentIndex) =>
        currentIndex === index ? cloneFilter({ ...current, ...patch }) : filter,
      )
    },
    removeFilter: (index) => {
      if (index < 0 || index >= filters.value.length) return
      filters.value = filters.value.filter((_filter, currentIndex) => currentIndex !== index)
    },
    clearFilters: () => {
      filters.value = []
    },
    resetFilters: () => {
      filters.value = initial()
    },
    filtered: (rows) => filterRows(rows ?? toValue(options.rows) ?? [], filters.value),
  }
}
